import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PlusCircle } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

interface SectorPickerProps {
  value: string;
  onChange: (value: string) => void;
}

export const SectorPicker = ({ value, onChange }: SectorPickerProps) => {
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customSector, setCustomSector] = useState("");
  const { toast } = useToast();

  const { data: sectors, refetch } = useQuery({
    queryKey: ["sectors"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sectors")
        .select("*")
        .order("name");
      
      if (error) throw error;
      return data;
    },
  });

  const handleAddCustomSector = async () => {
    if (!customSector) {
      toast({
        title: "Error",
        description: "Please enter a sector name",
        variant: "destructive",
      });
      return;
    }

    const { data, error } = await supabase
      .from("sectors")
      .insert([{ name: customSector, is_custom: true }])
      .select()
      .single();

    if (error) {
      toast({
        title: "Error",
        description: "Failed to add custom sector",
        variant: "destructive",
      });
      return;
    }

    if (data) {
      toast({
        title: "Success",
        description: "Custom sector added successfully",
      });
      onChange(data.id);
      await refetch();
      setCustomSector("");
      setShowCustomInput(false);
    }
  };

  const handleSelectChange = (newValue: string) => {
    if (newValue === "other") {
      setShowCustomInput(true);
    } else {
      onChange(newValue);
    }
  };

  return (
    <div className="space-y-2">
      {!showCustomInput ? (
        <Select value={value} onValueChange={handleSelectChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select sector" />
          </SelectTrigger>
          <SelectContent>
            {sectors?.map((sector) => (
              <SelectItem key={sector.id} value={sector.id}>
                {sector.name}
              </SelectItem>
            ))}
            <SelectItem value="other">
              <div className="flex items-center gap-2">
                <PlusCircle className="h-4 w-4" />
                Add Custom Sector
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      ) : (
        <div className="flex gap-2">
          <Input
            value={customSector}
            onChange={(e) => setCustomSector(e.target.value)}
            placeholder="Enter custom sector"
            className="flex-1"
          />
          <Button onClick={handleAddCustomSector}>Add</Button>
          <Button 
            variant="outline" 
            onClick={() => {
              setShowCustomInput(false);
              setCustomSector("");
            }}
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
};